import GlobalError, { logError } from '@app/src/components/GlobalError'
import WfLoader from '@app/src/components/WfLoader'
import { useAccount } from '@app/src/context/AccountContext'
import RequestError from '@app/src/errors/RequestError'
import useIntercom from '@app/src/hooks/intercom'
import useSolutionAutoLogin from '@app/src/hooks/solutionAutoLogin'
import useLaunchDarkly from '@app/src/hooks/useLaunchDarkly'
import { hideTransitionOverlay } from '@app/src/utils/transitionOverlay'

import { Box, Stack } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { lazy, Suspense, useEffect, useRef } from 'react'
import { useCookies } from 'react-cookie'
import { ErrorBoundary } from 'react-error-boundary'
import { Helmet } from 'react-helmet'
import type { RouteProps } from 'react-router-dom'
import { Route, Switch, useHistory, useLocation } from 'react-router-dom'
import type { RouteWithPermission } from './appRoutes'
import { Routes } from './appRoutes'
import InactivityCheck from './components/InactivityCheck'
import { BreadcrumbProvider } from './components/Navigation/Breadcrumbs/BreadcrumbContext'
import HorizontalMenu from './components/Navigation/HorizontalMenu/HorizontalMenu'
import LegacyBreadcrumbs from './components/Navigation/LegacyNavbar/LegacyBreadcrumbs'
import LegacyNavbar from './components/Navigation/LegacyNavbar/LegacyNavbar'
import VerticalMenu from './components/Navigation/VerticalMenu/VerticalMenu'
import { VerticalMenuPinProvider } from './components/Navigation/VerticalMenu/VerticalMenuContext'
import { Config } from './config'
import ForbiddenPage from './pages/Errors/403'
import NotFoundPage from './pages/Errors/404'
import { APP_MAIN_SCROLL_ELEMENT_ID, CONTENT_SCROLL_CONTAINER_ID } from './wf-constants'

const ConfigureAccountDialog = lazy(() => import('@app/src/pages/CreateOrganization/ConfigureAccountDialog'))

const HAS_RELOADED_COOKIE = 'reloaded-for-missing-chunks'

const IntercomUpdater = (): null => {
  // updates intercom for every route
  const { pathname } = useLocation()

  useEffect(() => {
    if (!window.Intercom) {
      return
    }
    window.Intercom('update')
  }, [pathname])

  return null
}

const RouteWithErrorBoundary = (props: RouteProps) => {
  const history = useHistory()
  const titlePrefix = Config.FRONT_ENV !== 'prod' ? `[${Config.FRONT_ENV}] ` : ''
  const titleTemplate = `${titlePrefix}%s - Worldfavor`
  const [cookies, setCookies] = useCookies([HAS_RELOADED_COOKIE])

  return (
    <>
      <Helmet titleTemplate={titleTemplate} defaultTitle={`${titlePrefix}Worldfavor`} />
      <ErrorBoundary
        FallbackComponent={GlobalError}
        onError={(error, info): void => {
          const isChunkLoadError =
            error.name === 'ChunkLoadError' ||
            error.message?.toLowerCase().includes('dynamically imported module') ||
            error.message?.includes('Importing a module script failed')
          if (isChunkLoadError) {
            // forcing the browser to reload if a chunk is missing, most likely due to a recent deployment
            // setting a cookie to avoid an infinite loop in case the chunk is really missing
            if (!cookies[HAS_RELOADED_COOKIE]) {
              setCookies(HAS_RELOADED_COOKIE, 1, { maxAge: 60 /*seconds*/ })
              window.location.reload()
              return
            }
          }
          logError(error, info)

          if (error instanceof RequestError && error.statusCode === 401) {
            history.replace('/logout')
          }
        }}
      >
        <Box flexGrow={1} display="flex" flexDirection="column">
          <Suspense
            fallback={
              <>
                <WfLoader />
              </>
            }
          >
            <Route {...props} />
          </Suspense>
        </Box>
      </ErrorBoundary>
    </>
  )
}

export const App: React.FC = () => {
  useIntercom()
  const { hasPermission, account } = useAccount()
  const { pathname } = useLocation()
  const { verticalNavigation = false } = useFlags()

  useSolutionAutoLogin()
  useLaunchDarkly()

  const prevPathnameRef = useRef(pathname)
  useEffect(() => {
    if (prevPathnameRef.current === pathname) return
    prevPathnameRef.current = pathname
    const frameId = requestAnimationFrame(() => {
      hideTransitionOverlay({ force: true })
    })
    return () => cancelAnimationFrame(frameId)
  }, [pathname])

  // Routed content, shared between the vertical-nav and legacy layouts
  const routedContent = (
    <>
      <Route path="/" component={IntercomUpdater} />
      {!account ? (
        <Box display="flex" alignItems="center" justifyContent="center" flexGrow={1}>
          <WfLoader />
        </Box>
      ) : (
        <>
          <Switch>
            {Routes.map((route: RouteWithPermission) => {
              if (route.permission && !hasPermission(route.permission)) {
                return (
                  <Route exact={route.exact} key={route.name} path={route.path}>
                    <ForbiddenPage />
                  </Route>
                )
              }

              return (
                <RouteWithErrorBoundary
                  exact={route.exact}
                  key={route.name}
                  path={route.path}
                  component={route.component}
                />
              )
            })}
            <Route>
              <NotFoundPage />
            </Route>
          </Switch>
          {account.organization && (
            <Suspense fallback={null}>
              <ConfigureAccountDialog key={account.organization.id} />
            </Suspense>
          )}
        </>
      )}
    </>
  )

  // Legacy navigation (top navbar). Gated behind the `verticalNavigation` flag for the
  // pilot rollout. To fully release the vertical nav: drop this branch and delete the
  // components/Navigation/LegacyNavbar folder.
  if (!verticalNavigation) {
    return (
      <Box
        id={APP_MAIN_SCROLL_ELEMENT_ID}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          backgroundColor: 'grey.100',
          overflow: 'auto',
        }}
      >
        <Route path="/">
          <InactivityCheck />
          <BreadcrumbProvider>
            <LegacyNavbar />
            <LegacyBreadcrumbs />
            {routedContent}
          </BreadcrumbProvider>
        </Route>
      </Box>
    )
  }

  return (
    <Box
      id={APP_MAIN_SCROLL_ELEMENT_ID}
      sx={{
        display: 'flex',
        height: '100vh',
        backgroundColor: 'grey.100',
        overflow: 'auto',
      }}
    >
      <Route path="/">
        <InactivityCheck />
        <VerticalMenuPinProvider>
          <VerticalMenu />
          <BreadcrumbProvider>
            <Stack
              id={CONTENT_SCROLL_CONTAINER_ID}
              sx={{ flexGrow: 1, minWidth: 0, overflowX: 'hidden', overflowY: 'auto' }}
            >
              <HorizontalMenu />
              {routedContent}
            </Stack>
          </BreadcrumbProvider>
        </VerticalMenuPinProvider>
      </Route>
    </Box>
  )
}

export default App
